import RootStore from "stores/RootStore";
import { DialogButton } from "components/Button";
import { Info } from "@mui/icons-material";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import DepartmentProductionScheduleStore from "../DepartmentProductionScheduleStore";
import { observer } from "mobx-react";

// Combined status types from production schedules and work orders
type ScheduleStatus =
  | "planned"
  | "in_progress"
  | "completed"
  | "cancelled"
  | "pending"
  | "scheduled"
  | "rejected"
  | "on_hold";

interface EntityProps {
  id: string;
  productName: string;
  productCode: string;
  scheduleDate: string;
  departmentId: string;
  formId: string;
  formName: string;
  quantity: number;
  status: ScheduleStatus;
  // Source type to distinguish between production schedules and work orders
  sourceType?: "production_schedule" | "work_order";
  // Work order specific fields
  workOrderNumber?: string;
  pieceNumber?: number;
  priority?: number;
  // Sales order fields
  jobName?: string;
  orderNumber?: string;
}

interface DepartmentProductionScheduleRightBodyCellCellEntityProps {
  entity: EntityProps;
  rowIndex: number;
  rootStore?: RootStore;
  style?: React.CSSProperties;
}

const DepartmentProductionScheduleRightBodyCellCellEntity = observer(
  (props: DepartmentProductionScheduleRightBodyCellCellEntityProps) => {
    const entity = props.entity as EntityProps;
    const { rootStore } = props;
    const departmentProductionScheduleStore =
      rootStore?.departmentProductionScheduleStore as DepartmentProductionScheduleStore;

    const width = props?.style?.width as number;

    const isWorkOrder = entity.sourceType === "work_order";

    // Use real status for color coding
    const statusColor: Record<ScheduleStatus, string> = {
      // Production schedule statuses
      planned: "#2196f3", // Blue - Planned
      in_progress: "#ff9800", // Orange - In Progress
      completed: "#4caf50", // Green - Completed
      cancelled: "#9e9e9e", // Gray - Cancelled
      // Work order statuses
      pending: "#9c27b0", // Purple - Pending
      scheduled: "#673ab7", // Deep Purple - Scheduled
      rejected: "#f44336", // Red - Rejected
      on_hold: "#795548", // Brown - On Hold
    };
    const borderColor = statusColor[entity.status] || (isWorkOrder ? "#673ab7" : "#2196f3");

    const currentEntityIsSelected = departmentProductionScheduleStore.currentlySelectedEntities.includes(entity.id);

    return (
      <div
        key={entity.id}
        style={{
          height: 52,
          width: width - 8,
          border: `2px solid ${borderColor}`,
          borderRadius: 4,
          margin: 2,
          display: "flex",
          alignItems: "stretch",
          backgroundColor: "white",
        }}
      >
        {/* Checkbox */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            borderRight: "1px solid #eee",
          }}
        >
          <Checkbox
            checked={currentEntityIsSelected}
            onChange={() => {
              departmentProductionScheduleStore.setCurrentlySelectedEntities(entity.id);
            }}
            sx={{ padding: "4px", "& .MuiSvgIcon-root": { fontSize: 18 } }}
          />
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: "4px 8px",
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          {/* Top Row: Product Name + Job Name */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            {/* Left: Badge + Product Name */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                overflow: "hidden",
                minWidth: 0,
              }}
            >
              {/* SO/WO Badge */}
              <Box
                sx={{
                  backgroundColor: isWorkOrder ? "#673ab7" : "#2196f3",
                  color: "white",
                  fontSize: "9px",
                  fontWeight: "bold",
                  px: 0.5,
                  py: 0.25,
                  borderRadius: 0.5,
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                {isWorkOrder ? "WO" : "SO"}
              </Box>

              {/* Product Name (bold) */}
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={entity?.productName}
              >
                {entity?.productName}
                {entity?.quantity > 1 && (
                  <span style={{ marginLeft: 4, fontSize: "11px", color: "#666", fontWeight: 400 }}>
                    x{entity.quantity}
                  </span>
                )}
              </span>
            </Box>

            {/* Right: Job Name */}
            {entity?.jobName && (
              <span
                style={{
                  fontSize: "11px",
                  color: "#666",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  maxWidth: "40%",
                }}
                title={entity.jobName}
              >
                {entity.jobName}
              </span>
            )}
          </Box>

          {/* Bottom Row: Product Code / Descriptor */}
          <span
            style={{
              fontSize: "11px",
              color: "#666",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={entity?.productCode}
          >
            {entity?.productCode}
          </span>
        </Box>

        {/* Info Button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            borderLeft: "1px solid #eee",
          }}
        >
          <DialogButton
            justIcon
            color="transparent"
            dialogName="productDetails"
            dialogContext={entity.id.toString()}
            style={{ padding: "4px", minWidth: "32px" }}
          >
            <Info style={{ fontSize: "18px" }} />
          </DialogButton>
        </Box>
      </div>
    );
  }
);

export default DepartmentProductionScheduleRightBodyCellCellEntity;
